import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useNavigate } from 'react-router-dom';

import { InviteOnboardingModal, SideNav } from '../components';
import { RootState } from '../stores/store';
import { convertModulesToOptions } from '../utils/common';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import useUserList from '../hooks/api/useUserList';
import UserAuth from '../services/auth';
import activityTracker from '../services/activityLogger';
import useUserDetails from '../hooks/api/useUserDetails';
import useAuth from '../hooks/useAuth';
import useUserModules from '../hooks/api/useUserModules';
import { useUserLogoutMutation } from '@api/userDetails';
import { USER_APPROVAL_STATUS } from '@constants/constants';
import { NAV_ROUTES } from '../constants/navRoutes';
import { clearDeepLinkStorage } from '../pages/DeepLinkRedirect/utils';

interface MainLayoutProps {
  children?: ReactNode;
}

const MainLayout: FC<MainLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const appData = useSelector((state: RootState) => state?.AppData);
  const userDetails = useSelector((state: RootState) => state?.UserData);
  const modules = useSelector((state: RootState) => state?.ModulesData);
  const { getUserModules } = useUserModules();
  const { getUserList } = useUserList();
  const { getUserDetails } = useUserDetails();
  const [userLogout] = useUserLogoutMutation();
  const { loginUrl } = useAuth();

  const [showInviteOnboardingModal, setShowInviteOnboardingModal] = useState(false);

  // Show invite onboarding modal when invited user has not yet accepted terms of service
  useEffect(() => {
    const shouldShow =
      Boolean(userDetails?.user?.id) && userDetails?.user?.termsOfServiceAccepted == null;
    setShowInviteOnboardingModal(shouldShow);
  }, [userDetails?.user]);

  const token = appData?.auth?.access_token || UserAuth.accessToken || '';

  // Hook to fetch user data for logged in user and also to fetch user management data if user is admin
  useEffect(() => {
    const fetchUserData = async () => {
      if (token) {
        if (!userDetails?.user?.id) {
          const response = await getUserDetails();
          if (response?.accessStatus === USER_APPROVAL_STATUS.PENDING_OWNER_APPROVAL) {
            navigate(NAV_ROUTES.ACCESS_PENDING);
            return;
          } else if (
            response?.accessStatus === USER_APPROVAL_STATUS.USER_PENDING_WORKSPACE_SETUP ||
            response?.accessStatus === USER_APPROVAL_STATUS.COMPANY_CREATION_REQUESTED
          ) {
            navigate(NAV_ROUTES.ACCESS_COMPANY_PENDING);
            return;
          } else if (response?.accessStatus === USER_APPROVAL_STATUS.REJECTED) {
            navigate(NAV_ROUTES.ACCESS_REJECTED);
            return;
          }
        } else if (userDetails?.user?.role === 'ADMIN') {
          getUserList({});
        }
      }
    };

    fetchUserData();
  }, [userDetails, token]);

  // Hook to fetch modules accessible to logged in user
  useEffect(() => {
    const fetchModules = async () => {
      if (
        modules?.modules?.length === 0 &&
        modules.loadCount === 0 &&
        userDetails?.user?.id &&
        token
      ) {
        await getUserModules(userDetails?.user?.role === 'ADMIN');
      }
    };

    fetchModules();
  }, [modules, userDetails, token]);

  // Convert modules to sidenav options dynamically
  const sidenavOptions = useMemo(
    () => convertModulesToOptions(modules?.modules ?? []),
    [modules?.modules],
  );

  // Function to clear token and log out user
  const logoutUser = async () => {
    // Sent before the pvx-core logout, after which the event tracker rejects the user's events
    await activityTracker.logout(true);
    await userLogout({});
    UserAuth.clearTokens();
    clearDeepLinkStorage();

    setTimeout(() => {
      window.location.href = loginUrl;
    }, 1000);
  };

  return (
    <Box
      component="main"
      id="main_layout_container"
      sx={{
        height: {
          xs: '100dvh',
          md: '100vh',
        },
        width: '100vw',
        overflow: 'hidden',
        display: 'flex',
        background: theme.palette.custom_background,
      }}
    >
      {/* Invite Onboarding Modal For Consent Acceptance And Newsletter Opt Ins */}
      {showInviteOnboardingModal && (
        <InviteOnboardingModal
          open={showInviteOnboardingModal}
          user={userDetails?.user}
          onClose={() => setShowInviteOnboardingModal(false)}
        />
      )}

      <SideNav
        options={sidenavOptions}
        isLoading={modules.loadCount === 0}
        isAdmin={userDetails?.user?.role === 'ADMIN'}
        username={userDetails?.user?.username || userDetails?.user?.email || ''}
        onClickLogout={() => logoutUser()}
        customWidth={isMobile ? '70%' : '300px'}
      />
      {isMobile ? (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            p: 0,
            m: 0,
            mt: theme.sizes.mobile.marginTop,
            pb: theme.sizes.mobile.marginTop,
          }}
        >
          {children ?? <Outlet />}
        </Box>
      ) : (
        <Box
          sx={{
            width: 'calc(100% - 100px)',
            overflow: 'hidden',
            p: 0,
            m: 0,
            flex: 1,
          }}
        >
          {children ?? <Outlet />}
        </Box>
      )}
    </Box>
  );
};

export default MainLayout;
